import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { useNavigate } from 'react-router'
import type { GrantableScope, TeamMember } from '@/api/pending'
import { useAccess } from '@/access/useAccess'
import { SUBTITLES } from '@/shell/sectionCopy'
import { Avatar, Button, Card, Icon, PageHeader, Pill, Row, Spinner } from '@/ui'
import { teamQuery } from './api'
import { GrantModal } from './GrantModal'
import { RevokeModal } from './RevokeModal'
import { S, SCOPE_DESCRIPTION, SCOPE_LABEL } from './strings'
import styles from './StaffView.module.css'

const SCOPES: GrantableScope[] = ['admin', 'reviewer', 'doorman']

export function StaffView() {
  const { condoId, membership, me } = useAccess()
  const navigate = useNavigate()
  const { data: team, isPending } = useQuery(teamQuery(condoId))
  const [granting, setGranting] = useState(false)
  const [revoking, setRevoking] = useState<TeamMember | null>(null)

  return (
    <>
      <PageHeader
        title="Equipe e permissões"
        subtitle={SUBTITLES.staff({ condoName: membership.condominium_name, waiting: 0 })}
        actions={
          <>
            <Button
              variant="secondary"
              icon="key"
              onClick={() => void navigate(`/c/${condoId}/staff/permissions`)}
            >
              {S.permissions}
            </Button>
            <Button icon="add" onClick={() => setGranting(true)}>
              {S.grant}
            </Button>
          </>
        }
      />
      {isPending ? (
        <div className={styles.loading}>
          <Spinner />
        </div>
      ) : (
        <Card pad={0} className={styles.list}>
          {team?.map((m) => (
            <Row key={m.scope_id}>
              <Avatar name={m.name} size={40} />
              <div className={styles.person}>
                <div className={styles.name}>{m.name}</div>
                <div className={styles.email}>{m.email}</div>
              </div>
              <Pill tone={m.scope === 'admin' ? 'brand' : 'neutral'}>{SCOPE_LABEL[m.scope]}</Pill>
              <Button
                size="s"
                variant="ghost"
                aria-label={`${S.revoke} ${m.name}`}
                onClick={() => setRevoking(m)}
              >
                {S.revoke}
              </Button>
            </Row>
          ))}
        </Card>
      )}
      <div className={styles.explainers}>
        {SCOPES.map((scope) => (
          <Card key={scope} pad={20}>
            <div className={styles.explainerHead}>
              <Icon name="key" size={18} color="var(--color-primary)" />
              <span className={styles.explainerTitle}>{SCOPE_LABEL[scope]}</span>
            </div>
            <div className={styles.explainerText}>{SCOPE_DESCRIPTION[scope]}</div>
          </Card>
        ))}
      </div>

      {granting && <GrantModal condoId={condoId} onClose={() => setGranting(false)} />}
      {revoking && (
        <RevokeModal
          condoId={condoId}
          member={revoking}
          team={team ?? []}
          myUserId={me.user?.id}
          onClose={() => setRevoking(null)}
        />
      )}
    </>
  )
}
